import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const payrollKeys = {
  all: ['payroll'],
  signature: () => [...payrollKeys.all, 'signature'],
  template: (orgId) => [...payrollKeys.all, 'template', orgId],
  templateRecords: (orgId, unmatched) => [...payrollKeys.all, 'template-records', orgId, unmatched],
  runs: (orgId) => [...payrollKeys.all, 'runs', orgId],
  assignedToMe: () => [...payrollKeys.all, 'runs', 'assigned-to-me'],
  run: (id) => [...payrollKeys.all, 'run', id],
  runRecords: (id) => [...payrollKeys.all, 'run-records', id],
  runSummary: (id) => [...payrollKeys.all, 'run-summary', id],
  myPayslips: (orgId) => [...payrollKeys.all, 'my-payslips', orgId],
  myPayslip: (id) => [...payrollKeys.all, 'my-payslip', id],
};

// ===== SIGNATURE =====
// Any signatory user can upload/preview/remove their signature

export function useMySignature() {
  return useQuery({
    queryKey: payrollKeys.signature(),
    queryFn: () => apiHelper.getResource('/payroll/signature/'),
    retry: false,
  });
}

export function useUploadSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('signature', file);
      return apiHelper.postResource('/payroll/signature/', formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.signature() });
    },
  });
}

export function useDeleteSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiHelper.deleteResource('/payroll/signature/'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.signature() });
    },
  });
}

// ===== TEMPLATE =====
// Finance uploads Excel; HR views and edits records

export function usePayrollTemplate(orgId, { enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.template(orgId),
    queryFn: () => apiHelper.getResource('/payroll/template/', { orgId }),
    enabled: !!orgId && enabled,
    retry: false,
  });
}

export function usePayrollTemplateRecords(orgId, { unmatched = false, enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.templateRecords(orgId, unmatched),
    queryFn: () => {
      const url = unmatched
        ? '/payroll/template/records/?unmatched=true'
        : '/payroll/template/records/';
      return apiHelper.getResource(url, { orgId });
    },
    enabled: !!orgId && enabled,
    select: (data) => (Array.isArray(data) ? data : data?.results || []),
  });
}

export function useUploadPayrollTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, label, notes, orgId }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('label', label);
      if (notes) formData.append('notes', notes);
      return apiHelper.postResource('/payroll/template/upload/', formData, { orgId });
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.template(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, false) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, true) });
    },
  });
}

export function useUpdateTemplateRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) =>
      apiHelper.patchResource(`/payroll/template/records/${id}/`, data, { orgId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, false) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, true) });
    },
  });
}

export function useAddTemplateRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) =>
      apiHelper.postResource('/payroll/template/records/', data, { orgId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, false) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, true) });
    },
  });
}

export function useDeleteTemplateRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) =>
      apiHelper.deleteResource(`/payroll/template/records/${id}/`, { orgId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, false) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, true) });
    },
  });
}



export function useDownloadPayrollTemplate() {
  return useMutation({
    mutationFn: ({ orgId, label }) => {
      const filename = `Payroll_Template_${(label || 'Current').replace(/\s+/g, '_')}.xlsx`;
      return apiHelper.downloadResource('/payroll/template/download/excel/', filename, { orgId });
    },
  });
}

export function useDeletePayrollTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, force = false }) =>
      apiHelper.deleteResource('/payroll/template/delete/', {
        orgId,
        ...(force && { params: { force: true } }),
      }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.template(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, false) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.templateRecords(orgId, true) });
    },
  });
}

// ===== PAYROLL RUNS =====

export function usePayrollRuns(orgId, { enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.runs(orgId),
    queryFn: () => apiHelper.getResource('/payroll/runs/', { orgId }),
    enabled: !!orgId && enabled,
    select: (data) => (Array.isArray(data) ? data : data?.results || []),
  });
}

// Runs where the current user is the assigned reviewer or approver.
// Not org-scoped — this is how a cross-org assignee finds runs waiting on them.
export function usePayrollRunsAssignedToMe({ enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.assignedToMe(),
    queryFn: () => apiHelper.getResource('/payroll/runs/?assigned_to_me=true'),
    enabled,
    select: (data) => (Array.isArray(data) ? data : data?.results || []),
  });
}

export function usePayrollRun(runId, orgId) {
  return useQuery({
    queryKey: payrollKeys.run(runId),
    queryFn: () => apiHelper.getResource(`/payroll/runs/${runId}/`, { orgId }),
    enabled: !!runId && !!orgId,
  });
}

export function usePayrollRunRecords(runId, orgId, { enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.runRecords(runId),
    queryFn: () => apiHelper.getResource(`/payroll/runs/${runId}/records/`, { orgId }),
    enabled: !!runId && !!orgId && enabled,
    select: (data) => (Array.isArray(data) ? data : data?.results || []),
  });
}

export function usePayrollRunSummary(runId, orgId, { enabled = true } = {}) {
  return useQuery({
    queryKey: payrollKeys.runSummary(runId),
    queryFn: () => apiHelper.getResource(`/payroll/runs/${runId}/summary/`, { orgId }),
    enabled: !!runId && !!orgId && enabled,
  });
}

export function useTriggerPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) =>
      apiHelper.postResource('/payroll/runs/', data, { orgId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
    },
  });
}

// Reassign reviewer/approver — only while status is draft or rejected.
export function useUpdatePayrollRunAssignees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) =>
      apiHelper.patchResource(`/payroll/runs/${id}/`, data, { orgId }),
    onSuccess: (_, { id, orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.run(id) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

export function useDeletePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) =>
      apiHelper.deleteResource(`/payroll/runs/${id}/`, { orgId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

// Advances the chain to the next reviewer, or to `submitted` if this was the last one.
export function useReviewPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) =>
      apiHelper.postResource(`/payroll/runs/${id}/review/?org_id=${orgId}`, data, { orgId }),
    onSuccess: (_, { id, orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.run(id) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

export function useApprovePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) =>
      apiHelper.postResource(`/payroll/runs/${id}/approve/`, {}, { orgId }),
    onSuccess: (_, { id, orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.run(id) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

export function useRejectPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, orgId }) =>
      apiHelper.postResource(`/payroll/runs/${id}/reject/`, { reason }, { orgId }),
    onSuccess: (_, { id, orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.run(id) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

export function usePublishPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) =>
      apiHelper.postResource(`/payroll/runs/${id}/publish/`, {}, { orgId }),
    onSuccess: (_, { id, orgId }) => {
      queryClient.invalidateQueries({ queryKey: payrollKeys.runs(orgId) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.run(id) });
      queryClient.invalidateQueries({ queryKey: payrollKeys.assignedToMe() });
    },
  });
}

export function useDownloadPayrollRun() {
  return useMutation({
    mutationFn: ({ id, label, orgId }) => {
      const filename = `Payroll_Run_${(label || id).replace(/\s+/g, '_')}.xlsx`;
      return apiHelper.downloadResource(`/payroll/runs/${id}/download/excel/`, filename, { orgId });
    },
  });
}

// ===== EMPLOYEE SELF-SERVICE PAYSLIPS =====

export function useMyPayslips(orgId) {
  return useQuery({
    queryKey: payrollKeys.myPayslips(orgId),
    queryFn: () => apiHelper.getResource('/payroll/my-payslips/', { orgId }),
    enabled: !!orgId,
    select: (data) => (Array.isArray(data) ? data : data?.results || []),
  });
}

export function useMyPayslip(id, orgId) {
  return useQuery({
    queryKey: payrollKeys.myPayslip(id),
    queryFn: () => apiHelper.getResource(`/payroll/my-payslips/${id}/`, { orgId }),
     enabled: !!id && !!orgId,
  });
}

export function useDownloadPayslip() {
  return useMutation({
    mutationFn: ({ id, employeeName, runLabel, orgId }) => {
      const name = (employeeName || 'Employee').replace(/\s+/g, '_');
      const label = (runLabel || id).replace(/\s+/g, '_');
      const filename = `Payslip_${name}_${label}.pdf`;
      return apiHelper.downloadResource(`/payroll/my-payslips/${id}/download/`, filename, { orgId });
          },
  });
}
